import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import useMediaQuery from '@mui/material/useMediaQuery';
import cn from 'classnames';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Circle, circleApi } from 'src/api/circles';
import { postsApi } from 'src/api/posts';
import useAppSelector from 'src/hooks/Redux/useAppSelector';

import { AuctionBidDetails } from 'src/api/auctionApi';
import { useBranding } from 'src/branding';
import { useCircleRoleView } from 'src/hooks/CircleRoleView/useCircleRoleView';
import { selectCircleDetailsData } from 'src/redux/slices/circleDetailsSlice/circleDetailsSlice';
import { AuctionSection } from './AuctionSection/AuctionSection';
import { AvatarComponent } from './AvatarComponent/AvatarComponent';
import { UserActions } from './UserActions/UserActions';
import { UserHeader } from './UserHeader/UserHeader';
import styles from './UserInfo.module.scss';
import { UserStats } from './UserStats/UserStats';

interface UserInfoProps extends Circle {
  auctionId: string;
  auctionData: any;
  auctionBidsInfo: AuctionBidDetails | null;
}

export const UserInfo: FC<UserInfoProps> = ({
  auctionId,
  auctionData,
  auctionBidsInfo,
}) => {
  const matches = useMediaQuery('(max-width:768px)');
  const isNarrowMobile = useMediaQuery('(max-width:545px)');
  const circleData = useAppSelector(selectCircleDetailsData);

  const {
    leader_name = '',
    tribe_name = '',
    leader_id = '',
    _id = '',
    tribe_avatar_url = '',
    account_type = '',
    description = '',
  } = circleData ?? {};
  const coverImage = tribe_avatar_url || '';
  const { isLeader, actualIsLeader } = useCircleRoleView(_id);
  const branding = useBranding();

  const [membersCount, setMembersCount] = useState<number | null>(null);
  const [challengesCount, setChallengesCount] = useState<number>(0);
  // Bumped by the Members modal after a removal, so the counts reload
  const [membersVersion, setMembersVersion] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!_id) {
      return;
    }

    let isMounted = true;

    const fetchCircleData = async () => {
      try {
        const postsResponse = await postsApi.getPostsForCircle(_id, 1);
        if (isMounted) {
          setChallengesCount(postsResponse.pagination.totalCount ?? 0);
        }
      } catch {
        // Members may still see the header without challenge metrics.
      }

      try {
        const membersResponse = actualIsLeader
          ? await circleApi.getMembersOfCircle(_id)
          : await circleApi.getCircleMemberDirectory(_id);
        if (isMounted) {
          const count = Array.isArray(membersResponse)
            ? membersResponse.length
            : 0;
          if (count > 0) {
            setMembersCount(count);
            return;
          }
        }
      } catch {
        // Fall through to contacts if the member directory is hidden.
      }

      try {
        const contacts = await circleApi.getCircleContacts(_id);
        if (isMounted) {
          const count = Array.isArray(contacts) ? contacts.length : 0;
          setMembersCount(count > 0 ? count : null);
        }
      } catch {
        if (isMounted) {
          setMembersCount(null);
        }
      }
    };

    void fetchCircleData();

    return () => {
      isMounted = false;
    };
  }, [_id, actualIsLeader, membersVersion]);

  useLayoutEffect(() => {
    if (isExpanded) {
      return;
    }
    const element = descriptionRef.current;
    if (!element) {
      setCanExpand(false);
      return;
    }
    setCanExpand(element.scrollHeight > element.clientHeight + 1);
  }, [description, isExpanded]);

  const userActions = (
    <UserActions
      isLeader={isLeader}
      tribeId={_id}
      leaderId={leader_id}
      leaderName={leader_name}
      auctionId={auctionId}
      tribeAvatar={tribe_avatar_url}
      account_type={account_type}
      onMembersChanged={() => setMembersVersion((v) => v + 1)}
    />
  );

  return (
    <div>
      <div
        className={cn(styles.container, {
          [styles.brandHero]: branding.hasHeaderSurface,
        })}
        style={{
          backgroundImage:
            matches && coverImage ? `url(${coverImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <AvatarComponent isLeader={isLeader} />
        <div className={styles.infoContent}>
          <UserHeader
            tribeId={_id}
            tribeName={tribe_name}
            isLeader={isLeader}
          />
          <UserStats
            membersCount={membersCount}
            maxMemberCount={circleData?.member_count_max}
            challengesCount={challengesCount}
          />
          {isLeader && !isNarrowMobile && userActions}
          {!matches && description && (
            <div className={styles.descWrapper}>
              <p
                ref={descriptionRef}
                className={cn(styles.desc, {
                  [styles.descCollapsed]: !isExpanded,
                })}
              >
                {description}
              </p>
              {(canExpand || isExpanded) && (
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className={styles.showMore}
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                  {isExpanded ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  )}
                </button>
              )}
            </div>
          )}
          {auctionId && auctionData && (
            <AuctionSection
              auctionId={auctionId}
              tribeId={_id}
              tribeName={tribe_name}
              leaderName={leader_name}
              tribeAvatar={tribe_avatar_url}
              auctionData={auctionData}
              auctionBidsInfo={auctionBidsInfo}
            />
          )}
        </div>
        {isNarrowMobile && isLeader && userActions}
      </div>
      {auctionId && auctionData && matches && (
        <AuctionSection
          auctionId={auctionId}
          tribeId={_id}
          tribeName={tribe_name}
          leaderName={leader_name}
          tribeAvatar={tribe_avatar_url}
          auctionData={auctionData}
          isMobile={true}
          auctionBidsInfo={auctionBidsInfo}
        />
      )}
    </div>
  );
};
